import React from 'react';
import _ from 'lodash';
import {
  Text,
  TextContent,
  TextList,
  TextListItem,
  TextListVariants,
  TextVariants,
  Title
} from '@patternfly/react-core';
import ReactMarkdown from 'react-markdown';
import { CodeEditor } from '@patternfly/react-code-editor';
import remarkGfm from 'remark-gfm';
import PropTypes from 'prop-types';
import {
  Table,
  Tbody,
  Thead,
  Td,
  Th,
  Tr
} from '@patternfly/react-table';

// Create h1...h6 functional components that wraps <Title headingLevel='h' + level for markdown
const headingLevels = _.reduce(_.range(1, 7),
  (result, heading) =>(
    {
      ...result,
      ['h' + heading]: ({ children }) => <Title headingLevel={'h' + heading}>{ children }</Title>
    }),
  {});

const ListItem = ({ children }) => <TextListItem>{ children }</TextListItem>;
const OrderedList = ({ children }) => <TextList component={TextListVariants.ol}>{ children }</TextList>;
const UnorderedList = ({ children }) => <TextList component={TextListVariants.ul}>{ children }</TextList>;
const TextVariant = ({ children, node }) => <Text component={node.tagName}>{ children }</Text>;

const onEditorDidMount = (editor, monaco) => {
  editor.layout();
  monaco.editor.getModels()[0].updateOptions({ tabSize: 4 });
};

const Code = ({ children, className }) => {
  const language = className?.split('-')[1];
  return (<CodeEditor
    isReadOnly
    height={'sizeToFit'}
    code={children[0]}
    language={language}
    onEditorDidMount={onEditorDidMount}
  />);
};

// Fixes error with missing aria-label
const _Table = ({ children }) => {
  return <Table aria-label={'table'}>{ children }</Table>;
};

// Fixes errors with property isHeader on DOM element
const skipNodeParam = (Component) => ({ children }) => <Component children={children} />;

// Overrides Text + TextVariant on this
const CustomLink = ({ children, ...otherProps }) => {
  return <a {...otherProps} >{ children }</a>;
};

const Markdown = ({ markdown }) => {

  return (<TextContent><ReactMarkdown
    linkTarget={'_blank'}
    components={{
      ..._.reduce(TextVariants, (result, variant) => ({ ...result, [variant]: TextVariant }), {}),
      ...headingLevels,
      li: ListItem,
      ol: OrderedList,
      ul: UnorderedList,
      code: Code,
      thead: Thead,
      tb: Tbody,
      td: skipNodeParam(Td),
      th: skipNodeParam(Th),
      table: _Table,
      tr: skipNodeParam(Tr),
      a: CustomLink
    }}
    remarkPlugins={[remarkGfm]}>
    { markdown }
  </ReactMarkdown></TextContent>);
};

Markdown.propTypes = {
  markdown: PropTypes.string
};

export default React.memo(Markdown);
